import { useState } from 'react';
import clsx from 'clsx';
import { NumberCircle } from '@/components/generic/NumberCircle';
import { ConsultingCompanyMaxProjectsCountProgressBar } from '@/components/project/project/ConsultingCompanyMaxProjectsCountProgressBar';
import { ChevronRightIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { useDateTimeFromIsoString } from '@/hooks/useDateTimeFromIsoString';
import { EmployeeItem } from './EmployeeItem';
import { DeleteCompanyButton } from '@/components/admin/company/DeleteCompanyButton';
import { InvitationModalButton } from '@/components/project/project/users/consultingCompany/InvitationModal';
import { HydratedConsultingCompany } from '@/slices/consultingCompany/consultingCompany.types';
import { CompanyProjectsSections } from './CompanyProjectsSections';

export function CompanyItem({
  company,
  index,
}: {
  company: HydratedConsultingCompany;
  index: number;
}) {
  const { t } = useTranslation();
  const createdAt = useDateTimeFromIsoString(company.createdAt);
  const [isExpanded, setIsExpanded] = useState(false);

  const createdBy = company.createdByResolved;

  return (
    <li className="flex items-center gap-2">
      <NumberCircle number={index + 1} size={8} />
      <div className="border border-gray-100 rounded-xl space-y-6 w-full bg-white">
        <div
          className="p-4 flex items-center gap-4 justify-between cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div>
            <h3 className="font-bold">{company.name}</h3>
            <div className="text-gray-500 text-xs">
              {t('admin:companies.list.item.createdAtAndBy', {
                createdAt: createdAt.date,
                createdBy: `${createdBy?.firstName} ${createdBy?.lastName}`,
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="min-w-60">
              <ConsultingCompanyMaxProjectsCountProgressBar
                companyId={company.id}
              />
            </div>
            <ChevronRightIcon className={clsx(isExpanded && 'rotate-90')} />
          </div>
        </div>

        {isExpanded && (
          <div className="space-y-4 divide-y">
            <div className="p-4 flex items-start gap-8 w-full divide-x divide-gray-100">
              <div className="w-1/2 space-y-2">
                <div className="flex items-center gap-4 justify-between">
                  <div className="flex items-center gap-1">
                    <h6>{t('admin:companies.list.item.employees')}</h6>
                    <div className="tag gray">
                      {company.employeesResolved.length}
                    </div>
                  </div>
                </div>
                <ul className="space-y-1 sami-body-small">
                  {company.employeesResolved.map((employee) => (
                    <EmployeeItem key={employee.id} employee={employee} />
                  ))}
                </ul>
                <InvitationModalButton consultingCompanyId={company.id} />
              </div>
              <div className="w-1/2 pl-8 space-y-4 divide-y divide-gray-100">
                <CompanyProjectsSections company={company} />
              </div>
            </div>
            <div className="p-4 flex justify-end">
              <DeleteCompanyButton companyId={company.id} />
            </div>
          </div>
        )}
      </div>
    </li>
  );
}
